import api from './api'; // Importando a instância do Axios com interceptores configurados.

export interface UserCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  cpf: string;
  perfil?: string;
  url_foto_perfil?: string;
  role?: string;
  status?: string;
  imagem?: File; // Arquivo da imagem
  sendPasswordResetEmail?: number
}

export interface User {
  id: number;
  name: string;
  email: string;
  perfil?: string;
  url_foto_perfil?: string;
  email_verified_at?: string | null;
  sendPasswordResetEmail: boolean;
  cpf: string;
  role?: string;
  status?: string;
  password: string;
  created_at: string;
  updated_at: string;
}

const authService = {

  async login(credentials: UserCredentials) {
    try {
      const response = await api.post('/login', credentials);
      const { token } = response.data;
      localStorage.setItem('token', token);

      api.defaults.headers['Authorization'] = `Bearer ${token}`;

      return {
        success: true,
        token,
        user: response.data.user,
      };
    } catch (error: any) {
      // Tratamento específico para erro 404 (endpoint não encontrado)
      if (error?.response?.status === 404) {
        console.error('❌ Erro 404: Endpoint /api/login não encontrado no backend');
        console.error('Verifique se:');
        console.error('1. O backend Laravel está rodando em http://localhost:8001');
        console.error('2. A rota /api/login está definida no arquivo routes/api.php');
        console.error('3. O CORS está configurado corretamente no backend');
        throw new Error('Endpoint de login não encontrado. Verifique se o backend está rodando e configurado corretamente.');
      }

      // Se houver token na resposta de erro (caso especial do backend)
      if (error?.response?.data?.token) {
        localStorage.setItem('token', error.response.data.token);
        api.defaults.headers['Authorization'] = `Bearer ${error.response.data.token}`;
      } else {
        // Log do erro para debug
        console.error('Erro no login:', {
          status: error?.response?.status,
          message: error?.response?.data?.message || error?.message,
          data: error?.response?.data
        });
      }

      throw error;
    }
  },

  // Método de logout
  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        await api.post('/logout', {}, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      // Limpar os dados de autenticação do localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Erro ao realizar logout', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getToken(): string | null {
    return localStorage.getItem('token');
  },

  async me() {
    const token = this.getToken();
    if (!token) return { valid: false, message: 'Token ausente ou inválido' };

    try {
      const response = await api.get('/me');
      return response.data;
    } catch (error: any) {
      // Tratamento específico para erro 404 (endpoint não encontrado)
      if (error?.response?.status === 404) {
        console.warn('⚠️ Endpoint /api/me não encontrado no backend');
        return { valid: false, message: 'Endpoint não encontrado' };
      }

      // Tratamento para erro 401 (não autorizado)
      if (error?.response?.status === 401) {
        console.warn('⚠️ Token inválido ou expirado');
        localStorage.removeItem('token');
        return { valid: false, message: 'Token inválido ou expirado' };
      }

      // Outros erros
      console.error('Erro ao buscar informações do usuário:', error?.response?.status || error?.message);
      return { valid: false, message: error?.response?.data?.message || 'Erro ao buscar informações do usuário' };
    }
  },


  async register(data: RegisterData) {
    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('cpf', data.cpf);

      // Compat: alguns backends usam "perfil" em vez de "role"
      const effectiveRole = data.role ?? data.perfil;
      if (data.perfil) formData.append('perfil', data.perfil);
      if (data.url_foto_perfil) formData.append('url_foto_perfil', data.url_foto_perfil);
      if (data.role) formData.append('role', data.role);
      if (effectiveRole && !data.perfil) formData.append('perfil', effectiveRole);
      if (effectiveRole && !data.role) formData.append('role', effectiveRole);
      if (data.status) formData.append('status', data.status);

      // Se houver um arquivo de imagem, anexa ao FormData
      if (data.imagem) {
        formData.append('imagem', data.imagem);
      }

      const response = await api.post('/users', formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          // Deixe o Axios setar o boundary automaticamente
          'Content-Type': 'multipart/form-data',
        },
      });


      return response;
    } catch (error) {
      throw new Error('Erro ao registrar usuário. Tente novamente.');
    }
  },



  // Método para Atualizar Usuário
  async updateUser(id: number, data: Partial<RegisterData>) {
    try {
      const token = localStorage.getItem('token');

      // Laravel/PHP frequentemente não parseia body de PUT com consistência,
      // então padronizamos em POST + _method=PUT (multipart) com/sem imagem.
      const formData = new FormData();
      formData.append('_method', 'PUT');
      const effectiveRole = data.role ?? data.perfil;

      if (data.name !== undefined) formData.append('name', data.name);
      if (data.email !== undefined) formData.append('email', data.email);
      if (data.cpf !== undefined) formData.append('cpf', data.cpf);
      if (data.perfil !== undefined && data.perfil !== null) formData.append('perfil', data.perfil);
      if (data.url_foto_perfil !== undefined && data.url_foto_perfil !== null) formData.append('url_foto_perfil', data.url_foto_perfil);
      if (data.role !== undefined && data.role !== null) formData.append('role', data.role);
      if (effectiveRole !== undefined && effectiveRole !== null) {
        if (data.perfil === undefined || data.perfil === null) formData.append('perfil', effectiveRole);
        if (data.role === undefined || data.role === null) formData.append('role', effectiveRole);
      }
      if (data.status !== undefined && data.status !== null) formData.append('status', data.status);
      if (data.imagem) formData.append('imagem', data.imagem);

      return await api.post(`/users/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      throw new Error('Erro ao atualizar usuário. Tente novamente.');
    }
  },

  // Método para Obter Todos os Usuários
  async getAllUsers() {
    try {
      const response = await api.get('/users', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      return response.data.users;
    } catch (error) {
      throw new Error('Erro ao obter usuários. Tente novamente.');
    }
  },

    async getUserById(id:number) {
    try {
      const response = await api.get(`/users/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      return response.data.user;
    } catch (error) {
      throw new Error('Erro ao obter usuários. Tente novamente.');
    }
  },

    async updatePassword(id: number, data: { password: string; }) {
      try {
        const response = await api.post('/users/update-password/'+id, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        return response.data;
      } catch (error) {
        throw new Error('Erro ao atualizar senha. Tente novamente.');
      }
    },
  
  // Método para Redefinir Senha
  async resetPassword(id: number) {
    try {
      const response = await api.post(`/users/${id}/reset-password`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      return response.data;
    } catch (error) {
      throw new Error('Erro ao redefinir a senha. Tente novamente.');
    }
  },
};



export default authService;
